import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Form,
  Input,
  Button,
  Upload,
  notification,
  Select,
  Spin,
  Image,
} from "antd";
import { UploadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import http from "../../helpers/http";

const { TextArea } = Input;
const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/signature-spaces`;
const image_url = `${API_URL}uploads/images/`;

function SignatureSpaceEdit() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await http.get(`${base_url}/${id}`);
        const item = res.data || res;
        setData(item);
        form.setFieldsValue({
          title: item.title,
          description: item.description,
          orderId: item.orderId,
          status: item.status,
          productLink: item.productLink,
        });
      } catch (error) {
        notification.error({
          message: "Error fetching Signature Space",
          description: error?.message,
        });
        navigate("/signature-spaces");
      } finally {
        setFetching(false);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id, form, navigate]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title || "");
      formData.append("description", values.description || "");
      formData.append("orderId", values.orderId || 0);
      formData.append("status", values.status || "active");
      formData.append("productLink", values.productLink || "");

      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("img", fileList[0].originFileObj);
      }

      await http.put(`${base_url}/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      notification.success({ message: "Signature Space updated successfully" });
      navigate("/signature-spaces");
    } catch (error) {
      notification.error({
        message: "Error updating Signature Space",
        description: error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  if (fetching) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to="/signature-spaces">Signature Spaces</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Edit</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title="Edit Signature Space"
        extra={
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/signature-spaces")}
          >
            Back
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: "Please input the title!" }]}
              >
                <Input placeholder="Signature Space title" />
              </Form.Item>
            </Col>
            
            <Col xs={24} md={12}>
              <Form.Item
                label="Order ID"
                name="orderId"
                rules={[{ required: true, message: "Please input the order ID!" }]}
              >
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="Description"
                name="description"
              >
                <TextArea rows={4} placeholder="Signature Space description" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Image Upload">
                <Upload
                  listType="picture"
                  maxCount={1}
                  fileList={fileList}
                  onChange={handleUploadChange}
                  beforeUpload={() => false}
                  customRequest={dummyRequest}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>Replace Image (Optional)</Button>
                </Upload>
                {data?.img && !fileList?.length && (
                  <div style={{ marginTop: 10 }}>
                    <p>Current Image:</p>
                    <Image width={100} src={image_url + data.img} />
                  </div>
                )}
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
                <Form.Item label="Product Link" name="productLink">
                    <Input placeholder="/product/example-slug" />
                </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
                <Form.Item label="Status" name="status">
                    <Select>
                        <Option value="active">Active</Option>
                        <Option value="inactive">Inactive</Option>
                    </Select>
                </Form.Item>
            </Col>

            <Col xs={24}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Signature Space
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
}

export default SignatureSpaceEdit;
